import React, { useCallback } from 'react';
import { Button, CircularProgress, Typography, Box, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components for enhanced UI
const StyledResendButton = styled(Button)(({ theme, buttonType }) => ({
  minWidth: '140px',
  height: '36px',
  borderRadius: '8px',
  fontWeight: 600,
  fontSize: '14px',
  textTransform: 'none',
  position: 'relative',
  transition: 'all 0.3s ease',

  ...(buttonType === 'ready' && {
    background: 'linear-gradient(45deg, #1D2426 30%, #1D2426 90%)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(45deg, #1D2426 30%, #1D2426 90%)',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 8px rgba(71, 72, 73, 0.3)',
    },
  }),

  ...(buttonType === 'cooldown' && {
    background: 'linear-gradient(45deg, #1D2426 30%, #1D2426 90%)',
    color: 'white',
    cursor: 'default',
    '&:hover': {
      background: 'linear-gradient(45deg, #1D2426 30%, #1D2426 90%)',
    },
  }),

  ...(buttonType === 'blocked' && {
    background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)',
    color: 'white',
    cursor: 'not-allowed',
    '&:hover': {
      background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)',
    },
  }),
}));

const MessageBox = styled(Box)(({ theme, messageType }) => ({
  padding: '8px 12px',
  borderRadius: '6px',
  marginBottom: '8px',
  fontSize: '12px',
  fontWeight: 500,

  ...(messageType === 'ready' && {
    background: 'rgba(33, 150, 243, 0.1)',
    color: '#1D2426',
    border: '1px solid rgba(33, 150, 243, 0.2)',
  }),

  ...(messageType === 'cooldown' && {
    background: 'rgba(255, 152, 0, 0.1)',
    color: '#1D2426',
    border: '1px solid rgba(255, 152, 0, 0.2)',
  }),

  ...(messageType === 'blocked' && {
    background: 'rgba(244, 67, 54, 0.1)',
    color: '#D32F2F',
    border: '1px solid rgba(244, 67, 54, 0.2)',
  }),
}));

const OtpResendButton = ({
  cooldownHook,
  onResend,
  isLoading = false,
  showMessage = true,
  size = 'medium',
  variant = 'contained'
}) => {
  const {
    canResend,
    isOnCooldown,
    isBlocked,
    formattedTime,
    message,
    startCooldown,
    remainingTime,
    nextDelay // total cooldown time for this attempt
  } = cooldownHook;

  const handleResend = () => {
    if (!canResend || isLoading) return;
    startCooldown();
    if (onResend) onResend();
  };

  const getButtonText = useCallback(() => {
    if (isLoading) return (
      <span style={{ color: 'white' }}>
        Sending...
      </span>
    );
    if (isBlocked) return (
      <span style={{ color: 'white' }}>
        🚫 Blocked
      </span>
    );
    if (isOnCooldown) return (
      <span style={{ color: 'white', display: 'flex', alignItems: 'center' }}>
        <span role="img" aria-label="timer" style={{ color: 'white', marginRight: 4 }}>⏱️</span>
        {formattedTime}
      </span>
    );
    return (
      <span style={{ color: 'white' }}>
        <span role="img" aria-label="resend" style={{ color: 'white', marginRight: 4 }}>📱</span> Resend OTP
      </span>
    );
  }, [isLoading, isBlocked, isOnCooldown, formattedTime]);

  const getButtonType = useCallback(() => {
    if (isBlocked) return 'blocked';
    if (isOnCooldown) return 'cooldown';
    return 'ready';
  }, [isBlocked, isOnCooldown]);

  const isDisabled = !canResend || isLoading;

  // Calculate progress bar width
  const progressPercent = isOnCooldown && nextDelay > 0
    ? Math.max(0, Math.min(100, ((remainingTime / nextDelay) * 100)))
    : 0;

  return (
    <Box sx={{ width: '100%' }}>
      {showMessage && message && (
        <MessageBox messageType={message.type}>
          <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 600 }}>
            {message.title}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '11px', mt: 0.5, opacity: 0.8 }}>
            {message.message}
          </Typography>
        </MessageBox>
      )}

      <Tooltip
        title={
          isBlocked ? "Too many attempts. Please wait before trying again." :
            isOnCooldown ? `Please wait ${formattedTime} before resending` :
              "Click to send a new verification code"
        }
        arrow
      >
        <span>
          <StyledResendButton
            size={size}
            variant={variant}
            buttonType={getButtonType()}
            onClick={handleResend}
            disabled={isDisabled}
            fullWidth
          >
            {getButtonText()}
            {isLoading && (
              <CircularProgress
                size={16}
                sx={{
                  color: 'white',
                  position: 'absolute',
                  right: 8,
                }}
              />
            )}
          </StyledResendButton>
        </span>
      </Tooltip>

      {/* Progress indicator for cooldown */}
      {isOnCooldown && (
        <Box
          sx={{
            mt: 1,
            height: '2px',
            background: 'rgba(255, 152, 0, 0.2)',
            borderRadius: '1px',
            overflow: 'hidden',
            color: 'white',
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: `${progressPercent}%`,
              background: 'linear-gradient(45deg, #1D2426, #1D2426)',
              transition: 'width 1s linear'
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default OtpResendButton;
